import { SeoHead, organizationLd, webSiteLd, webPageLd } from '../seo';

export default function Privacy() {
  return (
    <>
      <SeoHead
        title="Privacy Policy — Parksy.rent"
        description="How Parksy handles your data: hashed IP, encrypted contact info, public API rounding, no advertising, no third-party data sharing."
        canonical="https://parksy.rent/privacy"
        ogImage="https://parksy.rent/og/privacy.png"
        schemas={[organizationLd(), webSiteLd(), webPageLd({ name: 'Privacy Policy', description: 'How Parksy handles your data.', url: 'https://parksy.rent/privacy' })]}
      />
      <div className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Privacy Policy</h1>
        <div className="prose text-gray-600 space-y-4">
          <p>
            Parksy.rent is built on the principle of minimal data collection. We believe you
            should not have to hand over your identity to discover or list a parking spot.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-6">What we collect</h2>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong>IP address:</strong> Hashed with a secret salt to identify your pins for
              edit/delete and to prevent spam. Never displayed publicly.
            </li>
            <li>
              <strong>Email & phone:</strong> Required only when you list a spot or drop a seeker pin.
              Shared only with matched users via email. Never shown on the map.
            </li>
            <li>
              <strong>Location:</strong> The coordinates you place on the map. Public responses round
              coordinates to protect privacy.
            </li>
            <li>
              <strong>Listing details:</strong> Parking type, vehicle type, price, security features,
              access hours. Displayed publicly (except email/phone).
            </li>
          </ul>

          <h2 className="text-xl font-semibold text-gray-900 mt-6">How matching shares your contact</h2>
          <p>
            When you list a spot or drop a seeker pin, our matching engine compares your pin with
            others within a 3 km radius. When a compatible match is found, both parties receive a
            single email containing each other's email address and phone number. This is the only
            way the matching feature can work.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-6">"I'm interested" button</h2>
          <p>
            When you click "I'm interested" on a parking spot, you share your name (optional),
            email, phone, and a short message (optional) directly with the spot's owner. We send a
            single confirmation email to both of you with each other's contact details, and CC our
            team for quality monitoring. The owner can then reach out to you. We do not expose your
            interest on the public map, but we do display a non-identifying count (e.g. "3 people
            are interested") on the spot's popup.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-6">Anonymous comments</h2>
          <p>
            Any visitor can post a comment on a parking spot, seeker pin, or price-data pin. The
            only public fields are an optional display name and the comment body. If you leave the
            display name blank, your comment appears as "Anonymous". We never publish your email,
            phone, or IP. Links in comments are automatically stripped. Comments reported by 3 or
            more distinct visitors are auto-hidden pending review. You can delete your own
            comments at any time.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-6">Your rights</h2>
          <ul className="list-disc pl-5 space-y-2">
            <li>Delete your own pins anytime.</li>
            <li>Request deletion of all your data by emailing hi@parksy.rent.</li>
            <li>Browse and search without providing any personal information.</li>
          </ul>

          <h2 className="text-xl font-semibold text-gray-900 mt-6">Data retention</h2>
          <p>
            Listing pins expire after 90 days unless renewed. Seeker pins expire after 30 days.
            IP hashes are retained for abuse prevention. Contact information is kept until your pin
            is deleted or you request removal.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-6">Analytics</h2>
          <p>
            We use Google Analytics 4 to understand aggregated, anonymous usage patterns — for
            example, which features people use and where flows break. No personal data is sent.
            IP addresses are anonymized before reaching Google. No advertising features are
            enabled, and we do not share any of your listing, seeker, or contact data with
            Google. You can opt out using browser-level tools such as content blockers or the
            Google Analytics Opt-out Browser Add-on.
          </p>

          <p className="mt-6 text-sm text-gray-500">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>
      </div>
    </>
  );
}
